// Lead Nurture — Full-page Lead Workspace.
// Open Lead → Understand Lead → Take Action → Record Outcome → Schedule Next Action.
// Clean, premium, mobile-friendly. Reuses the working nurture endpoints.
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, PhoneCall, Mail, MessageSquare, StickyNote, CalendarPlus,
  Rocket, XCircle, CheckCircle2, Clock, User, Activity, ArrowRight, Send,
  CalendarDays, Target, Moon, Sparkles, Hash, MapPin, Save, BellOff, UserPlus,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import {
  API, TEMP_META, effectiveTemp, computeNBA, NBA_TONE, computePipelineStage, STAGE_META,
  fmtDate, fmtDateTime, fmtRel, fmtMoney, daysSince, daysUntil, isPreReset, nurtureReturned,
} from '../components/nurture/command/helpers';
import { STATUS_LABELS } from '../components/nurture/command/LeadCard';

const STATUS_OPTIONS = [
  'new_lead', 'contacted', 'discovery', 'educating', 'pre_qualifying',
  'ready_to_proceed', 'long_term_nurture', 'lost', 'converted',
];

const NEXT_ACTION_OPTIONS = [
  'Call back', 'Send SMS', 'Send email', 'Book discovery call', 'Confirm discovery call',
  'Send client portal', 'Chase outstanding documents', 'Review borrowing capacity',
  'Prepare proposal', 'Long-term nurture', 'No action required',
];

const ACT_ICON = {
  call: PhoneCall, sms: MessageSquare, email: Mail, note: StickyNote,
  status_change: ArrowRight, follow_up_scheduled: CalendarPlus, follow_up_completed: CheckCircle2,
  follow_up_cancelled: XCircle, sequence_started: Send, snoozed: Clock, score_updated: Activity,
};

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Interaction note form
  const [noteText, setNoteText] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [nextDate, setNextDate] = useState('');
  const noteRef = useRef(null);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }), [token]);

  const load = useCallback(async () => {
    if (!token || !leadId) return;
    try {
      const [lr, ar, fr] = await Promise.all([
        fetch(`${API}/api/nurture/leads/${leadId}`, { headers: auth.headers }),
        fetch(`${API}/api/nurture/leads/${leadId}/activities?limit=100`, { headers: auth.headers }),
        fetch(`${API}/api/nurture/follow-ups?lead_id=${leadId}`, { headers: auth.headers }),
      ]);
      if (!lr.ok) throw new Error('Lead not found');
      setLead(await lr.json());
      setActivities(await ar.json().then((d) => (Array.isArray(d) ? d : [])).catch(() => []));
      setFollowUps(await fr.json().then((d) => (Array.isArray(d) ? d : [])).catch(() => []));
      setError(null);
    } catch (e) { setError(e.message || 'Failed to load lead'); }
    finally { setLoading(false); }
  }, [leadId, token, auth]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/nurture/leads/${leadId}`, { method: 'PATCH', headers: auth.headers, body: JSON.stringify(body) });
      if (!r.ok) throw new Error();
      toast.success(msg || 'Updated'); await load();
    } catch { toast.error('Update failed'); } finally { setBusy(false); }
  };

  const logQuick = async (action) => {
    try { await fetch(`${API}/api/nurture/leads/${leadId}/quick-action?action=${action}`, { method: 'POST', headers: auth.headers }); load(); } catch { /* noop */ }
  };

  const saveInteraction = async () => {
    if (!noteText.trim() && !nextAction && !nextDate) { toast.error('Add a note, next action or date'); return; }
    setBusy(true);
    try {
      if (noteText.trim()) {
        const r = await fetch(`${API}/api/nurture/activities`, { method: 'POST', headers: auth.headers, body: JSON.stringify({ lead_id: leadId, activity_type: 'note', description: noteText.trim() }) });
        if (!r.ok) throw new Error();
      }
      if (nextAction) {
        await fetch(`${API}/api/nurture/leads/${leadId}`, { method: 'PATCH', headers: auth.headers, body: JSON.stringify({ next_action: nextAction }) });
      }
      if (nextDate) {
        await fetch(`${API}/api/nurture/follow-ups`, { method: 'POST', headers: auth.headers, body: JSON.stringify({ lead_id: leadId, scheduled_at: new Date(nextDate).toISOString(), method: 'Call' }) });
        if (lead?.no_follow_up) await fetch(`${API}/api/nurture/leads/${leadId}`, { method: 'PATCH', headers: auth.headers, body: JSON.stringify({ no_follow_up: false }) });
      }
      setNoteText(''); setNextAction(''); setNextDate('');
      toast.success('Interaction recorded'); await load();
    } catch { toast.error('Could not save interaction'); } finally { setBusy(false); }
  };

  const scheduleQuick = async (days) => {
    const d = new Date(); d.setDate(d.getDate() + days); d.setHours(9, 0, 0, 0);
    setBusy(true);
    try {
      await fetch(`${API}/api/nurture/follow-ups`, { method: 'POST', headers: auth.headers, body: JSON.stringify({ lead_id: leadId, scheduled_at: d.toISOString(), method: 'Call' }) });
      if (lead?.no_follow_up) await fetch(`${API}/api/nurture/leads/${leadId}`, { method: 'PATCH', headers: auth.headers, body: JSON.stringify({ no_follow_up: false }) });
      toast.success('Follow-up scheduled'); await load();
    } catch { toast.error('Failed'); } finally { setBusy(false); }
  };

  const completeFU = async (fuId) => {
    setBusy(true);
    try { await fetch(`${API}/api/nurture/follow-ups/${fuId}/complete?outcome=spoke`, { method: 'POST', headers: auth.headers }); toast.success('Follow-up completed'); await load(); }
    catch { toast.error('Failed'); } finally { setBusy(false); }
  };

  const moveToPortal = async () => {
    if (!window.confirm('Mark this lead as Proceeding to the Client Portal? They will leave the active nurture queue.')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/nurture/leads/${leadId}/proceed-to-portal`, { method: 'POST', headers: auth.headers, body: JSON.stringify({}) });
      if (!r.ok) throw new Error();
      toast.success('Lead proceeding to Client Portal'); navigate('/lead-nurture');
    } catch { toast.error('Failed'); setBusy(false); }
  };

  const closeLead = async () => {
    if (!window.confirm('Mark this lead as No Longer Interested?')) return;
    await patch({ status: 'lost' }, 'Lead closed — No longer interested');
  };

  const noFollowUp = async () => {
    setBusy(true);
    try {
      await Promise.all(followUps.filter((f) => f.status === 'pending').map((f) =>
        fetch(`${API}/api/nurture/follow-ups/${f.id}/complete?outcome=no_follow_up_needed`, { method: 'POST', headers: auth.headers })));
      const r = await fetch(`${API}/api/nurture/leads/${leadId}`, { method: 'PATCH', headers: auth.headers, body: JSON.stringify({
        no_follow_up: true, no_follow_up_at: new Date().toISOString(),
        next_follow_up_at: '', next_action_due: '', next_action: 'No follow-up needed',
      }) });
      if (!r.ok) throw new Error();
      toast.success('No follow-up needed — removed from Today');
      navigate('/lead-nurture');
    } catch { toast.error('Failed'); setBusy(false); }
  };

  const reactivateHistorical = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/nurture/leads/${leadId}/reactivate`, { method: 'POST', headers: auth.headers, body: JSON.stringify({}) });
      if (!r.ok) throw new Error();
      toast.success('Lead reactivated into the active workflow'); await load();
    } catch { toast.error('Reactivation failed'); } finally { setBusy(false); }
  };

  const focusNote = () => { noteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => noteRef.current?.focus(), 400); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Header />
        <main className="max-w-6xl mx-auto px-4 py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline-block text-slate-400" /></main>
      </div>
    );
  }
  if (error || !lead) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Header />
        <main className="max-w-6xl mx-auto px-4 py-16">
          <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center">
            <p className="text-slate-900 font-black mb-2">Lead not found</p>
            <p className="text-sm text-slate-500">{error}</p>
            <button type="button" onClick={() => navigate('/lead-nurture')} className="inline-block mt-4 text-blue-600 font-bold hover:underline">← Back to Lead List</button>
          </div>
        </main>
      </div>
    );
  }

  const nba = computeNBA(lead);
  const nbaTone = NBA_TONE[nba.tone] || NBA_TONE.slate;
  const temp = TEMP_META[effectiveTemp(lead)];
  const stage = STAGE_META[computePipelineStage(lead)] || {};
  const pendingFU = followUps.find((f) => f.status === 'pending');
  const historical = lead.nurture_phase === 'historical' || isPreReset(lead);
  const returned = nurtureReturned(lead);
  const lastContact = lead.last_contacted_at || lead.first_contacted_at;
  const nextContact = pendingFU?.scheduled_at || lead.next_follow_up_at || lead.next_action_due;

  const FACTS = [
    { icon: Activity, label: 'Current status', value: STATUS_LABELS[lead.status] || lead.status || '—' },
    { icon: Clock, label: 'Last contact', value: lastContact ? `${fmtDate(lastContact)} · ${fmtRel(lastContact)}` : 'Not yet contacted', tone: daysSince(lastContact) > 14 ? 'text-rose-700' : 'text-slate-900' },
    { icon: CalendarDays, label: 'Next scheduled contact', value: nextContact ? `${fmtDate(nextContact)} · ${fmtRel(nextContact)}` : 'None scheduled', tone: nextContact && daysUntil(nextContact) < 0 ? 'text-rose-700' : 'text-slate-900' },
    { icon: PhoneCall, label: 'Contact attempts', value: (lead.contact_count ?? lead.contact_attempts ?? 0).toString() },
    { icon: MapPin, label: 'Lead source', value: lead.lead_source || 'Unknown' },
    { icon: CalendarDays, label: 'Date received', value: fmtDate(lead.created_at) },
    { icon: User, label: 'Assigned broker', value: lead.assigned_broker_name || 'Unassigned' },
    { icon: Target, label: 'Loan purpose', value: lead.loan_purpose || lead.lead_type || '—' },
  ];

  const saveContact = () => {
    const full = (lead.name || 'Lead').trim();
    const parts = full.split(/\s+/);
    const first = parts[0] || '';
    const last = parts.length > 1 ? parts.slice(1).join(' ') : '';
    const vcf = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `N:${last};${first};;;`,
      `FN:${full}`,
      lead.phone ? `TEL;TYPE=CELL:${lead.phone}` : '',
      lead.email ? `EMAIL;TYPE=INTERNET:${lead.email}` : '',
      'ORG:DerwentAI Lead',
      lead.lead_number ? `NOTE:Lead ${lead.lead_number}` : '',
      'END:VCARD',
    ].filter(Boolean).join('\r\n');
    const url = URL.createObjectURL(new Blob([vcf], { type: 'text/vcard;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${full.replace(/[^a-z0-9]+/gi, '_') || 'contact'}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button type="button" onClick={() => navigate('/lead-nurture')} data-testid="ln-detail-back"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-600 hover:text-slate-900 transition-colors mb-4">
          <ArrowLeft className="w-4 h-4" /> Return to Lead List
        </button>

        {/* ═══ Hero / identity ═══ */}
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="brand-dark relative overflow-hidden rounded-3xl bg-slate-900 text-white shadow-[0_20px_60px_-20px_rgba(2,6,23,0.4)] p-6 sm:p-8" data-testid="lead-workspace-hero">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-rose-500/20 blur-3xl" aria-hidden />
          <div className="relative flex flex-col lg:flex-row lg:items-start justify-between gap-5">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full text-white ${temp.badge}`}>
                  <temp.icon className="w-3 h-3" /> {temp.label}
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 text-white/90">{stage.label || 'Lead'}</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono text-white/50"><Hash className="w-3 h-3" />{lead.lead_number || (lead.id || '').slice(0, 8)}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight" style={{ color: '#ffffff' }} data-testid="lead-workspace-name">{lead.name || 'Unnamed lead'}</h1>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-[13px] text-white/85">
                {lead.phone && <a href={`tel:${lead.phone}`} onClick={() => logQuick('call')} className="inline-flex items-center gap-1.5 hover:text-white transition-colors"><PhoneCall className="w-3.5 h-3.5" />{lead.phone}</a>}
                {lead.email && <a href={`mailto:${lead.email}`} onClick={() => logQuick('email')} className="inline-flex items-center gap-1.5 hover:text-white transition-colors truncate max-w-[260px]"><Mail className="w-3.5 h-3.5" />{lead.email}</a>}
                <span className="inline-flex items-center gap-1.5 text-white/60"><CalendarDays className="w-3.5 h-3.5" />Received {fmtDate(lead.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Recommended next action */}
          <div className="relative mt-5 rounded-2xl bg-white/[0.08] ring-1 ring-white/15 backdrop-blur p-4 flex items-center gap-3" data-testid="lead-workspace-nba">
            <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><nba.icon className="w-5 h-5 text-white" strokeWidth={2.2} /></span>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/50 leading-none">Recommended next action</p>
              <p className="text-base font-black text-white mt-1 leading-tight">{nba.label}</p>
            </div>
            {nba.due && <span className="text-[11px] font-bold text-white/70 whitespace-nowrap">{fmtRel(nba.due)}</span>}
          </div>
        </motion.section>

        {/* ═══ Primary actions ═══ */}
        <section className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-2" data-testid="lead-workspace-actions">
          {lead.phone && <ActionBtn href={`tel:${lead.phone}`} onClick={() => logQuick('call')} icon={PhoneCall} label="Call Lead" tone="emerald" testid="action-call" />}
          {(lead.phone || lead.email) && <ActionBtn onClick={saveContact} icon={UserPlus} label="Save Contact" tone="blue" testid="action-save-contact" />}
          <ActionBtn onClick={focusNote} icon={StickyNote} label="Add Note" tone="slate" testid="action-add-note" />
          <ActionBtn onClick={() => scheduleQuick(1)} icon={CalendarPlus} label="Schedule Follow-Up" tone="blue" testid="action-schedule" disabled={busy} />
          {lead.phone && <ActionBtn href={`sms:${lead.phone}`} onClick={() => logQuick('sms')} icon={MessageSquare} label="Send SMS" tone="violet" testid="action-sms" />}
          {lead.email && <ActionBtn href={`mailto:${lead.email}`} onClick={() => logQuick('email')} icon={Mail} label="Send Email" tone="indigo" testid="action-email" />}
          <ActionBtn onClick={noFollowUp} icon={BellOff} label="No Follow-Up Needed" tone="slate" testid="action-no-follow-up" disabled={busy} />
          <ActionBtn onClick={moveToPortal} icon={Rocket} label="Lead Proceeding" tone="emeraldSolid" testid="action-proceed" disabled={busy} />
          <ActionBtn onClick={closeLead} icon={XCircle} label="No Longer Interested" tone="rose" testid="action-close" disabled={busy} />
        </section>

        {historical && (
          <div data-testid="workspace-historical-banner" className="mt-4 rounded-2xl p-4 ring-1 ring-amber-300 bg-amber-50 flex items-center gap-3">
            <Moon className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-[13px] font-bold text-amber-800 flex-1">Historical lead — kept for reference, not in the active workflow. No follow-ups or overdue alerts run until reactivated.</p>
            <button type="button" onClick={reactivateHistorical} disabled={busy} data-testid="workspace-reactivate"
              className="text-[12px] font-bold px-3.5 py-2 rounded-full bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors whitespace-nowrap">Reactivate Lead</button>
          </div>
        )}
        {lead.no_follow_up && (
          <div data-testid="workspace-no-follow-up-banner" className="mt-4 rounded-2xl p-4 ring-1 ring-slate-300 bg-slate-100 flex items-center gap-3">
            <BellOff className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <p className="text-[13px] font-bold text-slate-700 flex-1">No follow-up needed — this lead won’t appear in your Today list.</p>
            <button type="button" onClick={() => patch({ no_follow_up: false }, 'Follow-ups resumed')} disabled={busy} data-testid="workspace-resume-follow-up"
              className="text-[12px] font-bold px-3.5 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 transition-colors whitespace-nowrap">Resume Follow-Ups</button>
          </div>
        )}
        {returned && !historical && (
          <div data-testid="workspace-nurture-returned" className="mt-4 rounded-2xl p-4 ring-1 ring-rose-300 bg-rose-50 flex items-center gap-3">
            <Rocket className="w-4 h-4 text-rose-600 flex-shrink-0" />
            <p className="text-[13px] font-bold text-rose-700 flex-1">Nurture period ended {fmtRel(lead.nurture_until)} — time to re-engage.</p>
            <button type="button" onClick={() => patch({ nurture_until: '', status: 'contacted' }, 'Re-activated')} data-testid="workspace-reengage"
              className="text-[12px] font-bold px-3.5 py-2 rounded-full bg-rose-600 text-white hover:bg-rose-700 transition-colors">Re-engage</button>
          </div>
        )}

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* ═══ LEFT: facts + record + supplied info ═══ */}
          <div className="lg:col-span-3 space-y-5">
            {/* Key facts */}
            <Card title="Lead summary" icon={User}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4" data-testid="lead-workspace-facts">
                {FACTS.map((f) => (
                  <div key={f.label}>
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 flex items-center gap-1"><f.icon className="w-3 h-3" />{f.label}</p>
                    <p className={`text-[13px] font-bold mt-1 leading-snug ${f.tone || 'text-slate-900'}`}>{f.value}</p>
                  </div>
                ))}
              </div>
              {/* Change status inline */}
              <div className="mt-5 pt-4 border-t border-slate-100">
                <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 mb-2">Change status</p>
                <div className="flex flex-wrap gap-1.5">
                  {STATUS_OPTIONS.map((st) => (
                    <button key={st} type="button" data-testid={`workspace-status-${st}`} disabled={busy}
                      onClick={() => patch({ status: st }, `Status → ${STATUS_LABELS[st]}`)}
                      className={`text-[11px] font-bold px-2.5 py-1.5 rounded-full ring-1 transition-colors disabled:opacity-50 ${lead.status === st ? 'bg-slate-900 text-white ring-slate-900' : 'bg-white text-slate-700 ring-slate-200 hover:ring-slate-400'}`}>
                      {STATUS_LABELS[st]}
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            {/* Add Interaction Note — the star */}
            <Card title="Add Interaction Note" icon={StickyNote} accent>
              <div ref={noteRef} className="space-y-3" tabIndex={-1}>
                <div>
                  <label className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-500">What happened?</label>
                  <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={4}
                    placeholder="e.g. Called the client — spoke for 10 mins, keen to proceed, sending portal link today."
                    data-testid="interaction-note-text"
                    className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-rose-100 focus:border-slate-300 resize-y" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-500">Next action</label>
                    <select value={nextAction} onChange={(e) => setNextAction(e.target.value)} data-testid="interaction-next-action"
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-rose-100">
                      <option value="">Select next action…</option>
                      {NEXT_ACTION_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-500">Next contact date</label>
                    <input type="datetime-local" value={nextDate} onChange={(e) => setNextDate(e.target.value)} data-testid="interaction-next-date"
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-rose-100" />
                  </div>
                </div>
                <button type="button" onClick={saveInteraction} disabled={busy} data-testid="interaction-save"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 text-white text-sm font-bold px-5 py-3 hover:bg-slate-800 disabled:opacity-50 transition-colors shadow-sm">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Interaction
                </button>
              </div>
            </Card>

            {/* Lead-supplied info */}
            {(lead.notes || lead.loan_purpose || lead.lead_type) && (
              <Card title="Information supplied by the lead" icon={Sparkles}>
                {lead.notes ? <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{lead.notes}</p> : <p className="text-sm text-slate-400">No notes supplied.</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  {lead.lead_type && <Tag>{lead.lead_type}</Tag>}
                  {lead.loan_purpose && <Tag>{lead.loan_purpose}</Tag>}
                  {fmtMoney(lead.property_target_price_max) && <Tag>{fmtMoney(lead.property_target_price_max)}</Tag>}
                  {typeof lead.lead_score === 'number' && <Tag>Score {lead.lead_score}</Tag>}
                </div>
              </Card>
            )}
          </div>

          {/* ═══ RIGHT: appointments + activity history ═══ */}
          <div className="lg:col-span-2 space-y-5">
            <Card title="Appointments & follow-ups" icon={CalendarDays}>
              {followUps.length === 0 ? (
                <p className="text-sm text-slate-400">No follow-ups scheduled. Use <b className="text-slate-600">Schedule Follow-Up</b> above.</p>
              ) : (
                <ul className="space-y-2" data-testid="workspace-followups">
                  {followUps.slice(0, 8).map((f) => {
                    const overdue = f.status === 'pending' && daysUntil(f.scheduled_at) < 0;
                    return (
                      <li key={f.id} className={`rounded-xl p-3 ring-1 flex items-center gap-3 ${f.status === 'completed' ? 'bg-emerald-50 ring-emerald-200/60' : overdue ? 'bg-rose-50 ring-rose-200/60' : 'bg-slate-50 ring-slate-200/60'}`}>
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${f.status === 'completed' ? 'bg-emerald-500 text-white' : overdue ? 'bg-rose-500 text-white' : 'bg-blue-500 text-white'}`}>
                          {f.status === 'completed' ? <CheckCircle2 className="w-4 h-4" /> : <CalendarPlus className="w-4 h-4" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-bold text-slate-900 leading-tight">{f.method || 'Follow-up'} · {fmtDate(f.scheduled_at)}</p>
                          <p className="text-[11px] text-slate-500">{f.status === 'completed' ? 'Completed' : fmtRel(f.scheduled_at)}{f.note ? ` — ${f.note}` : ''}</p>
                        </div>
                        {f.status === 'pending' && (
                          <button type="button" onClick={() => completeFU(f.id)} disabled={busy} data-testid={`workspace-complete-fu-${f.id}`}
                            className="text-[11px] font-bold px-2.5 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 whitespace-nowrap transition-colors">Done</button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            <Card title="Communication & activity history" icon={Activity}>
              {activities.length === 0 ? (
                <p className="text-sm text-slate-400">No activity logged yet. Record your first interaction above.</p>
              ) : (
                <ul className="space-y-0" data-testid="workspace-timeline">
                  {activities.map((a, i) => {
                    const Icon = ACT_ICON[a.activity_type] || Activity;
                    return (
                      <li key={a.id || i} className="relative flex gap-3 pb-4">
                        {i < activities.length - 1 && <span className="absolute left-[15px] top-8 bottom-0 w-px bg-slate-200" aria-hidden />}
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center"><Icon className="w-3.5 h-3.5 text-slate-500" /></span>
                        <div className="min-w-0 flex-1 pt-0.5">
                          <p className="text-[13px] text-slate-800 leading-snug">{a.description}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">{a.broker ? `${a.broker} · ` : ''}{fmtDateTime(a.created_at)}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

const Card = ({ title, icon: Icon, accent, children }) => (
  <motion.section initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
    className={`rounded-3xl bg-white border p-5 sm:p-6 shadow-[0_2px_10px_-4px_rgba(15,23,42,0.05)] ${accent ? 'border-rose-200' : 'border-slate-200'}`}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className={`w-4 h-4 ${accent ? 'text-rose-500' : 'text-slate-500'}`} strokeWidth={2.2} />
      <h2 className="text-base font-black text-slate-900">{title}</h2>
    </div>
    {children}
  </motion.section>
);

const Tag = ({ children }) => (
  <span className="inline-flex items-center text-[11px] font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200">{children}</span>
);

const TONES = {
  slate: 'bg-white text-slate-800 ring-slate-200 hover:ring-slate-400 hover:bg-slate-50',
  emerald: 'bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100',
  emeraldSolid: 'bg-emerald-600 text-white ring-emerald-600 hover:bg-emerald-700',
  blue: 'bg-blue-50 text-blue-800 ring-blue-200 hover:bg-blue-100',
  violet: 'bg-violet-50 text-violet-800 ring-violet-200 hover:bg-violet-100',
  indigo: 'bg-indigo-50 text-indigo-800 ring-indigo-200 hover:bg-indigo-100',
  rose: 'bg-rose-50 text-rose-800 ring-rose-200 hover:bg-rose-100',
};

const ActionBtn = ({ href, onClick, icon: Icon, label, tone = 'slate', testid, disabled }) => {
  const cls = `flex flex-col items-center justify-center gap-1.5 rounded-2xl ring-1 py-3.5 px-2 text-center transition-colors disabled:opacity-50 ${TONES[tone]}`;
  const inner = (<><Icon className="w-5 h-5" strokeWidth={2.1} /><span className="text-[11px] font-bold leading-tight">{label}</span></>);
  if (href && !disabled) return <a href={href} onClick={onClick} data-testid={testid} className={cls}>{inner}</a>;
  return <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} className={cls}>{inner}</button>;
};
